import { z } from 'zod';
import { CsvView } from 'libs/end-to-end/src/csv-parse';
import { isBlankString } from 'libs/end-to-end/src/generic';
import { FilterDefinition } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { FilterDefinitionWithValues } from 'libs/end-to-end/src/types/databases/filters/filter-definition';

// Example:
// const graph: FilterDependencyGraph = {
//   A: ['B', 'D'],
//   B: ['C'],
//   C: [],
//   D: []
// };
// const graph2: FilterDependencyGraph = {
//   'job_title': ['department'],
//   'department': []
// };
export type FilterDependencyGraph = Record<string, string[]>;

// Example:
// const fullGraph: FilterFullDependencyGraph = {
//   A: ['B', 'D', 'C'],
//   B: ['C'],
//   C: [],
//   D: []
// };
export type FilterFullDependencyGraph = Record<string, string[]>;

// Null indicates a circular reference
export type BuildFilterFullGraphResult = FilterFullDependencyGraph | null;

// If a circular reference is detected, the result will be null
export const buildFilterFullDependencyGraphOrNullOnCircularReference = (
  graph: FilterDependencyGraph,
): BuildFilterFullGraphResult => {
  const fullGraph: FilterFullDependencyGraph = {};

  // Helper function to recursively build the full graph
  const buildGraph = (key: string, visited: Set<string>): string[] | null => {
    if (visited.has(key)) {
      // Circular reference detected
      return null;
    }

    if (fullGraph[key]) {
      // Already processed this key
      return fullGraph[key];
    }

    visited.add(key);
    const dependencies = graph[key] || [];
    const fullDependencies: string[] = [];

    for (const dependency of dependencies) {
      const subDependencies = buildGraph(dependency, new Set(visited));
      if (subDependencies === null) {
        // Circular reference in sub-dependencies
        return null;
      }
      fullDependencies.push(dependency, ...subDependencies);
    }

    // Remove duplicates
    fullGraph[key] = Array.from(new Set(fullDependencies));
    return fullGraph[key];
  };

  // Building the full graph for each key
  for (const key in graph) {
    if (buildGraph(key, new Set()) === null) {
      // Circular reference detected
      return null;
    }
  }

  return fullGraph;
};

export interface UniqueColumnMapValue {
  value: string;
  children?: UniqueColumnMap[];
}

export interface UniqueColumnMap {
  header: string;
  values: UniqueColumnMapValue[];
}

export const UniqueColumnMapValue: z.ZodType<UniqueColumnMapValue> = z.lazy(
  () =>
    z.object({
      value: z.string(),
      children: z.array(UniqueColumnMap).optional(),
    }),
);

export const UniqueColumnMap: z.ZodType<UniqueColumnMap> = z.lazy(() =>
  z.object({
    header: z.string(),
    values: z.array(UniqueColumnMapValue),
  }),
);

export const determineUniqueValues = ({
  csvView,
  columnHeaderValue,
  criteria,
}: {
  csvView: CsvView;
  columnHeaderValue: string;
  criteria: {
    columnHeaderValue: string;
    columnValue: string;
  }[];
}): string[] => {
  const result: string[] = [];

  for (const row of csvView.rows) {
    let rowMatches = true;

    for (const criterion of criteria) {
      const columnIndex = csvView.headersToIndexes.get(
        criterion.columnHeaderValue,
      );

      if (columnIndex === undefined) {
        rowMatches = false;
        break;
      }

      if (row[columnIndex] !== criterion.columnValue) {
        rowMatches = false;
        break;
      }
    }

    if (rowMatches) {
      const columnIndex = csvView.headersToIndexes.get(columnHeaderValue);

      if (columnIndex === undefined) {
        continue;
      }

      const val = isBlankString(row[columnIndex])
        ? '(blank)'
        : row[columnIndex];

      if (!result.includes(val)) {
        result.push(val);
      }
    }
  }

  return result;
};

export const produceUniqueValuesMap = (
  csvView: CsvView,
  graph: FilterDependencyGraph,
  graphKeys: string[],
  previousCriteria: {
    columnHeaderValue: string;
    columnValue: string;
  }[] = [],
): UniqueColumnMap[] => {
  const results: UniqueColumnMap[] = [];

  for (const key of graphKeys) {
    const columnIndex = csvView.headersToIndexes.get(key);

    if (columnIndex === undefined) {
      continue;
    }

    const uniqueValues = determineUniqueValues({
      csvView,
      columnHeaderValue: key,
      criteria: previousCriteria,
    });

    const dependents = Object.keys(graph).filter((k) => graph[k].includes(key));

    results.push({
      header: key,
      values: uniqueValues.map((value) => {
        const criteria = [{ columnHeaderValue: key, columnValue: value }];

        const children = produceUniqueValuesMap(
          csvView,
          graph,
          dependents,
          previousCriteria.concat(criteria),
        );

        return {
          value: value,
          children: children.length > 0 ? children : undefined,
        };
      }),
    });
  }

  return results;
};

export const determineOptionsAvailable = ({
  uniqueColumnMap,
  inputs,
  header,
  fallbackUniqueValues,
  graph,
}: {
  uniqueColumnMap: UniqueColumnMap[];
  // {
  //   department: ["Information Technology", "Admin"],
  //   job_title: []
  // }
  inputs: Record<string, string[]>;
  // department
  header: string;
  // ["foo", "bar"]
  fallbackUniqueValues: string[];
  graph: FilterDependencyGraph;
}): string[] => {
  // Resolve the complete path from the graph for the target header
  const mappedPath = mappedPathToKeyFromGraph(header, graph);

  if (mappedPath.length === 0) {
    // If the path is empty, return the fallback unique values
    return fallbackUniqueValues;
  }

  let currentMaps = uniqueColumnMap;

  // Traverse the path to narrow down to the correct context
  for (const pathHeader of mappedPath) {
    let found = false;
    for (const map of currentMaps) {
      if (map.header === pathHeader) {
        const inputValues = inputs[pathHeader] ?? [];
        // Filter the values based on the inputs
        const filteredValues = map.values.filter((value) =>
          inputValues.includes(value.value),
        );

        // Assume children of the first matched value are the new context
        currentMaps = filteredValues.flatMap((value) => value.children ?? []);
        found = true;
        break;
      }
    }
    if (!found) {
      // Path header not found, return fallback
      return fallbackUniqueValues;
    }
  }

  // At this point, currentMaps should be narrowed down to the context of the target header
  const headerMap = currentMaps.filter((m) => m.header === header);

  // Return the values if found, or fallback if the header is not within the path context
  return headerMap === undefined
    ? []
    : [...new Set(headerMap.flatMap((map) => map.values).map((v) => v.value))];
};

const mappedPathToKeyFromGraph = (
  key: string,
  graph: FilterDependencyGraph,
): string[] => {
  const walkPath = (k: string): string[] => {
    const newPath: string[] = [];

    const deps = graph[k] ?? [];

    for (const dep of deps) {
      newPath.push(dep);
      newPath.push(...walkPath(dep));
    }

    return newPath;
  };

  return walkPath(key).reverse();
};

export const buildDependencyGraph = (
  filterConfigs: FilterDefinition[],
): FilterDependencyGraph => {
  const graph: FilterDependencyGraph = {};

  for (const filterConfig of filterConfigs) {
    graph[filterConfig.columnHeaderValue] = filterConfig.dependsOn ?? [];
  }

  return graph;
};

// The whole point of this method is to produce what looks like a dependency graph
// but just serves as a shell without dependencies truly listed.
// We use this in event study to produce a list for the event study group menu.
export const buildIntentionallyNaiveDependencyGraph = (
  filterConfigs: FilterDefinition[],
): FilterDependencyGraph => {
  const graph: FilterDependencyGraph = {};

  for (const filterConfig of filterConfigs) {
    graph[filterConfig.columnHeaderValue] = [];
  }

  return graph;
};
